import {displayPaymentErrorMessage, type OrderService} from '../order';
import {Feature, stopModalLoading} from '../../reducers/environmentReducer';
import {getLocaleText} from '../../util/translation';
import {addButtonListener, defaultOrderFlow} from './peachpay';
import {$qs} from '../../../../@shared/ts/dom';
import {FeatureFlag} from '../../../../@type/features';
import purchaseOrderImageURL from '../../../img/marks/purchase-order.svg';
import {type GatewayConfiguration} from '../../models/GatewayConfiguration';
import {getErrorString} from '../../../../@shared/ts/error';
import {store} from '../../store';

export function initPurchaseOrderSupport(orderService: OrderService) {
	if (!Feature.enabled(FeatureFlag.PEACHPAY_PURCHASE_ORDER_GATEWAY)) {
		return;
	}

	injectPurchaseOrderFields();

	addButtonListener(getPurchaseOrderMethodConfiguration().gatewayId, async event => {
		const $form = $qs<HTMLFormElement>('#pp-pms-new form.pp-purchase-order-field');
		const $input = $qs<HTMLInputElement>('#pp-pms-new input[name="purchase_order_number"]');

		const {error: transactionError, result: transaction} = await orderService.startTransaction(getPurchaseOrderMethodConfiguration().gatewayId);
		if (transactionError || !transaction) {
			const errorMessage = transactionError ? getErrorString(transactionError) : getLocaleText('An unknown error occured while starting the transaction. Please refresh the page and try again.');
			displayPaymentErrorMessage(errorMessage);

			store.dispatch(stopModalLoading());
			return;
		}

		if (!$input || !$form) {
			await transaction.complete({note: 'Failed to find the Purchase Order input and form.'});
			return;
		}

		const purchaseOrderNumber = $input.value;
		if (!purchaseOrderNumber || !$form.checkValidity()) {
			await transaction.complete({note: 'Purchase Order number was missing or invalid.'});

			$form.reportValidity();

			return;
		}

		const $target = event.target as HTMLElement | null;
		const $button = $target?.closest<HTMLButtonElement>('button');
		if (!$button) {
			await transaction.complete({note: 'Purchase Order button was not found.'});
			return;
		}

		await defaultOrderFlow(orderService, transaction, {
			purchase_order_number: purchaseOrderNumber,
		});
	});
}

export function getPurchaseOrderMethodConfiguration(): GatewayConfiguration {
	return {
		name: getFieldName(),
		gatewayId: 'peachpay_purchase_order',
		description: `<span>${getDescription()}</span>`,
		assets: {
			title: {src: purchaseOrderImageURL},
			badge: {src: purchaseOrderImageURL},
		},
	};
}

function injectPurchaseOrderFields() {
	const newCustomerDiv = $qs('#pp-pms-new div.pp-pm-saved-option[data-gateway="peachpay_purchase_order"]');

	newCustomerDiv?.insertAdjacentHTML(
		'beforeend',
		/* Html */
		`<form class="pp-purchase-order-field" onsubmit="event.preventDefault()">
			<input id="pp-new-purchase-order-input" name="purchase_order_number" type="text" class="text-input" placeholder=" " required>
			<label for="pp-new-purchase-order-input" class="pp-form-label">${getFieldName()}</label>
		</form>`,
	);
}

function getFieldName(): string {
	return Feature.metadata(FeatureFlag.PEACHPAY_PURCHASE_ORDER_GATEWAY, 'field_name') ?? getLocaleText('Purchase order');
}

function getDescription(): string {
	return Feature.metadata(FeatureFlag.PEACHPAY_PURCHASE_ORDER_GATEWAY, 'description') ?? '';
}
